import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import type {
  MunicipalImplicationFinding,
  MunicipalImplicationKind,
  MunicipalImplicationLevel,
  MunicipalImplications,
  MunicipalImplicationsStatus,
} from "@/types/legislationMonitor";

type LocalImplicationsPaneProps = {
  analysis: MunicipalImplications | null;
  pendingMessage?: string;
  failedMessage?: string;
  emptyMessage?: string;
  notAvailableMessage?: string;
  supersededMessage?: string;
};

const KIND_META: Record<MunicipalImplicationKind, { order: number; label: string }> = {
  contradiction: { order: 0, label: "Conflict" },
  interaction: { order: 1, label: "Related Change" },
};

const IMPLICATION_META: Record<MunicipalImplicationLevel, { order: number; label: string }> = {
  high: { order: 0, label: "High Impact" },
  medium: { order: 1, label: "Medium Impact" },
  low: { order: 2, label: "Low Impact" },
};

const UNKNOWN_META = { order: Number.MAX_SAFE_INTEGER, label: "Unknown" };

function kindMeta(kind: string): { order: number; label: string } {
  return KIND_META[kind as MunicipalImplicationKind] ?? UNKNOWN_META;
}

function implicationMeta(level: string): { order: number; label: string } {
  return IMPLICATION_META[level as MunicipalImplicationLevel] ?? UNKNOWN_META;
}

function sortFindings(findings: MunicipalImplicationFinding[]): MunicipalImplicationFinding[] {
  return [...findings].sort((left, right) => {
    const kindDifference = kindMeta(left.kind).order - kindMeta(right.kind).order;
    if (kindDifference !== 0) return kindDifference;

    return implicationMeta(left.implication).order - implicationMeta(right.implication).order;
  });
}

function citationList(finding: MunicipalImplicationFinding): string[] {
  return [
    ...(finding.local_citations?.map((citation) => `City code: ${citation}`) ?? []),
    ...(finding.state_citations?.map((citation) => `State law: ${citation}`) ?? []),
  ];
}

function StatusCard({ message }: { message: string }) {
  return (
    <Card className="border-dashed border-muted-foreground/30 bg-muted/20">
      <CardContent className="p-6 text-sm text-muted-foreground">{message}</CardContent>
    </Card>
  );
}

function CompletedPane({
  analysis,
  emptyMessage,
}: {
  analysis: MunicipalImplications;
  emptyMessage: string;
}) {
  const findings = sortFindings(analysis.findings);

  return (
    <div className="space-y-4">
      {analysis.stale ? (
        <Alert>
          <AlertDescription>
            Your city code or the state-law analysis has changed since this was prepared. The findings
            below may be out of date. Ask Ordinal staff to refresh Local Implications.
          </AlertDescription>
        </Alert>
      ) : null}

      {analysis.gist?.trim() ? (
        <p className="text-sm font-medium leading-relaxed text-foreground">{analysis.gist}</p>
      ) : null}

      {findings.length > 0 ? (
        <ul className="space-y-3">
          {findings.map((finding, findingIndex) => {
            const citations = citationList(finding);

            return (
              <li
                key={`${finding.kind}-${finding.summary}-${findingIndex}`}
                className="rounded-md border p-4"
              >
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{kindMeta(finding.kind).label}</Badge>
                  <Badge variant="secondary">{implicationMeta(finding.implication).label}</Badge>
                </div>
                <p className="text-sm leading-relaxed text-foreground">{finding.summary}</p>
                {finding.implication_note?.trim() ? (
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {finding.implication_note}
                  </p>
                ) : null}
                {citations.length > 0 ? (
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                    {citations.map((citation, citationIndex) => (
                      <li key={`${citation}-${citationIndex}`}>{citation}</li>
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <StatusCard message={emptyMessage} />
      )}

      {analysis.caveats.length > 0 ? (
        <div className="text-sm text-muted-foreground">
          <p className="font-medium text-foreground">Things to keep in mind</p>
          <ul className="mt-1 list-disc space-y-1 pl-5">
            {analysis.caveats.map((caveat, index) => (
              <li key={`${caveat}-${index}`}>{caveat}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function LocalImplicationsPane({
  analysis,
  pendingMessage = "We are still reviewing how this bill may affect your city code.",
  failedMessage = "We could not finish the local review for this bill. Ordinal staff can try again.",
  emptyMessage = "No conflicts or related city-code changes turned up in the documents we searched. This is not legal clearance; staff should still review as needed.",
  notAvailableMessage = "A local review for this bill is not ready yet.",
  supersededMessage = "This local review was replaced or is no longer current. Ask Ordinal staff if you need an update.",
}: LocalImplicationsPaneProps) {
  if (!analysis) {
    return <StatusCard message={notAvailableMessage} />;
  }

  const status: MunicipalImplicationsStatus = analysis.status;
  switch (status) {
    case "pending":
    case "processing":
      return <StatusCard message={pendingMessage} />;
    case "failed":
      return (
        <Alert variant="destructive">
          <AlertDescription>{failedMessage}</AlertDescription>
        </Alert>
      );
    case "superseded":
      return <StatusCard message={supersededMessage} />;
    case "completed":
      return <CompletedPane analysis={analysis} emptyMessage={emptyMessage} />;
    default: {
      const _exhaustive: never = status;
      void _exhaustive;
      return <StatusCard message={notAvailableMessage} />;
    }
  }
}
